import type { Color } from 'antd/es/color-picker';

/**
 * RGB 转 HEX
 */
const rgbToHex = (r: number, g: number, b: number): string => {
  return `#${[r, g, b].map((x) => {
    const hex = x.toString(16);
    return hex.length === 1 ? `0${hex}` : hex;
  }).join('')}`;
};

/**
 * 将 ColorPicker 的 Color 对象转换为 linear-gradient 格式字符串（使用 HEX 格式）
 * 格式: linear-gradient(90deg, #108ee9 0%, #62bc90 69%, #87d068 100%)
 */
export const formatGradientToHex = (color: Color | string | undefined): string => {
  if (!color) {
    return '';
  }

  try {
    // 如果已经是字符串格式，检查并转换 RGB 为 HEX
    if (typeof color === 'string') {
      if (color.includes('linear-gradient')) {
        // 将字符串中的 rgb() 格式转换为 HEX 格式
        return color.replace(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/g, (match, r, g, b) => {
          return rgbToHex(parseInt(r, 10), parseInt(g, 10), parseInt(b, 10));
        });
      }
      // 如果是单个颜色（HEX），转换为渐变格式
      return `linear-gradient(90deg, ${color} 0%, ${color} 100%)`;
    }

    // Color 对象转换为渐变字符串
    const colorObj = color as any;
    
    // 优先使用 toCssString 方法（在渐变模式下会返回完整的 CSS 渐变字符串）
    if (typeof colorObj.toCssString === 'function') {
      const cssString = colorObj.toCssString();
      if (cssString && cssString.includes('linear-gradient')) {
        // 将 CSS 字符串中的 rgb 格式转换为 HEX 格式
        return cssString.replace(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/g, (match: string, r: string, g: string, b: string) => {
          return rgbToHex(parseInt(r, 10), parseInt(g, 10), parseInt(b, 10));
        });
      }
    }

    // 如果 toCssString 没有返回渐变，尝试使用 getGradient 方法
    if (typeof colorObj.getGradient === 'function') {
      const gradient = colorObj.getGradient();
      if (gradient && gradient.colors && gradient.colors.length > 0) {
        const angle = gradient.angle || 90;
        const colorStops = gradient.colors
          .map((stop: any) => {
            // 转换为 HEX 格式
            let hex: string;
            if (stop.color && typeof stop.color.toHexString === 'function') {
              hex = stop.color.toHexString();
            } else if (typeof stop.color === 'string') {
              hex = stop.color;
            } else {
              // 如果是 RGB 对象，转换为 HEX
              const rgb = stop.color?.toRgb?.() || stop.color;
              if (rgb && typeof rgb.r === 'number') {
                hex = rgbToHex(rgb.r, rgb.g, rgb.b);
              } else {
                hex = '#000000'; // 默认黑色
              }
            }
            return `${hex} ${stop.percent}%`;
          })
          .join(', ');

        return `linear-gradient(${angle}deg, ${colorStops})`;
      }
    }

    // 如果都没有，尝试从内部属性获取
    if (colorObj.gradient && colorObj.gradient.colors && colorObj.gradient.colors.length > 0) {
      const gradient = colorObj.gradient;
      const angle = gradient.angle || 90;
      const colorStops = gradient.colors
        .map((stop: any) => {
          let hex: string;
          if (stop.color && typeof stop.color.toHexString === 'function') {
            hex = stop.color.toHexString();
          } else if (typeof stop.color === 'string') {
            hex = stop.color;
          } else {
            const rgb = stop.color?.toRgb?.() || stop.color;
            if (rgb && typeof rgb.r === 'number') {
              hex = rgbToHex(rgb.r, rgb.g, rgb.b);
            } else {
              hex = '#000000';
            }
          }
          return `${hex} ${stop.percent}%`;
        })
        .join(', ');

      return `linear-gradient(${angle}deg, ${colorStops})`;
    }

    // 如果都无法获取渐变信息，返回空字符串
    return '';
  } catch (error) {
    console.error('格式化渐变字符串失败:', error);
    return '';
  }
};

/**
 * 解析 linear-gradient 字符串为 ColorPicker 可接受的格式
 * 格式: linear-gradient(90deg, #108ee9 0%, #62bc90 69%, #87d068 100%)
 * 或: linear-gradient(90deg, rgb(16,142,233) 0%, rgb(98,188,144) 69%, rgb(135,208,104) 100%)
 */
export const parseGradientString = (
  gradientString: string | undefined
): { color: string; percent: number }[] | undefined => {
  if (!gradientString || !gradientString.includes('linear-gradient')) {
    return undefined;
  }

  try {
    // 提取角度和颜色停止点
    const regex = /linear-gradient\((\d+)deg,\s*(.+)\)/;
    const match = gradientString.match(regex);
    if (!match) return undefined;

    const stops = match[2];
    const colorStops: { color: string; percent: number }[] = [];

    // 匹配 HEX 格式: #108ee9 0%
    const hexStopRegex = /#([a-f\d]{6})\s+(\d+(?:\.\d+)?)%/gi;
    let stopMatch;

    while ((stopMatch = hexStopRegex.exec(stops)) !== null) {
      const hex = stopMatch[1];
      const percent = parseFloat(stopMatch[2]);
      colorStops.push({
        color: `#${hex}`,
        percent,
      });
    }

    // 如果没有匹配到 HEX，尝试匹配 RGB 格式并转换为 HEX
    if (colorStops.length === 0) {
      const rgbStopRegex = /rgb\((\d+),(\d+),(\d+)\)\s+(\d+(?:\.\d+)?)%/g;
      while ((stopMatch = rgbStopRegex.exec(stops)) !== null) {
        const r = parseInt(stopMatch[1], 10);
        const g = parseInt(stopMatch[2], 10);
        const b = parseInt(stopMatch[3], 10);
        const percent = parseFloat(stopMatch[4]);
        const hex = rgbToHex(r, g, b);

        colorStops.push({
          color: hex,
          percent,
        });
      }
    }

    return colorStops.length > 0 ? colorStops : undefined;
  } catch (error) {
    console.error('解析渐变字符串失败:', error);
    return undefined;
  }
};

/**
 * 获取默认渐变颜色（HEX 格式，用于 ColorPicker）
 */
export const getDefaultGradient = (): { color: string; percent: number }[] => {
  return [
    {
      color: '#108EE9',
      percent: 0,
    },
    {
      color: '#87D068',
      percent: 100,
    },
  ];
};

/**
 * 限制渐变色数组为两种颜色（第一个和最后一个）
 */
export const limitGradientToTwoColors = (
  colors: { color: string; percent: number }[]
): { color: string; percent: number }[] => {
  if (colors.length <= 2) {
    // 如果已经是两种或更少，确保百分比正确
    if (colors.length === 2) {
      return [
        { ...colors[0], percent: 0 },
        { ...colors[1], percent: 100 },
      ];
    }
    return colors;
  }
  // 只保留第一个和最后一个，并设置百分比为 0% 和 100%
  return [
    { ...colors[0], percent: 0 },
    { ...colors[colors.length - 1], percent: 100 },
  ];
};

/**
 * 从 Color 对象提取渐变色数组，并限制为两种颜色
 */
export const extractGradientColors = (color: Color): { color: string; percent: number }[] => {
  try {
    const colorObj = color as any;
    let gradientColors: { color: string; percent: number }[] = [];

    // 尝试从 Color 对象获取渐变信息
    const getGradientFromColor = (obj: any): any => {
      if (typeof obj.getGradient === 'function') {
        return obj.getGradient();
      }
      if (obj.gradient) {
        return obj.gradient;
      }
      return null;
    };

    const gradient = getGradientFromColor(colorObj);
    if (gradient && gradient.colors && gradient.colors.length > 0) {
      gradientColors = gradient.colors.map((stop: any) => {
        let hex: string;
        if (stop.color && typeof stop.color.toHexString === 'function') {
          hex = stop.color.toHexString();
        } else if (typeof stop.color === 'string') {
          hex = stop.color;
        } else {
          const rgb = stop.color?.toRgb?.() || stop.color;
          if (rgb && typeof rgb.r === 'number') {
            hex = rgbToHex(rgb.r, rgb.g, rgb.b);
          } else {
            hex = '#000000';
          }
        }
        return {
          color: hex,
          percent: stop.percent || 0,
        };
      });
    }

    return limitGradientToTwoColors(gradientColors);
  } catch (error) {
    console.error('提取渐变颜色失败:', error);
    return getDefaultGradient();
  }
};

/**
 * 将 Color 对象转换为限制为两种颜色的 linear-gradient 字符串
 */
export const formatGradientToHexLimited = (color: Color | string | undefined): string => {
  if (!color) {
    return '';
  }

  // 如果是字符串，先解析再限制
  if (typeof color === 'string') {
    const parsed = parseGradientString(color);
    if (parsed) {
      const limited = limitGradientToTwoColors(parsed);
      const angle = 90;
      const colorStops = limited.map((stop) => `${stop.color} ${stop.percent}%`).join(', ');
      return `linear-gradient(${angle}deg, ${colorStops})`;
    }
    return color;
  }

  // 从 Color 对象提取颜色并限制
  const gradientColors = extractGradientColors(color);
  
  // 如果提取失败，使用 formatGradientToHex 并解析限制
  if (gradientColors.length === 0 || gradientColors.length === 1) {
    const fullGradientString = formatGradientToHex(color);
    const parsed = parseGradientString(fullGradientString);
    if (parsed) {
      const limited = limitGradientToTwoColors(parsed);
      const angle = 90;
      const colorStops = limited.map((stop) => `${stop.color} ${stop.percent}%`).join(', ');
      return `linear-gradient(${angle}deg, ${colorStops})`;
    }
    return fullGradientString;
  }

  // 格式化两种颜色的渐变字符串
  const angle = 90;
  const colorStops = gradientColors.map((stop) => `${stop.color} ${stop.percent}%`).join(', ');
  return `linear-gradient(${angle}deg, ${colorStops})`;
};

/**
 * HEX 颜色格式验证正则表达式
 */
const HEX_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/;

/**
 * 验证是否为有效的 HEX 颜色格式
 * @param color HEX 颜色字符串
 * @returns 是否为有效的 HEX 颜色
 */
export const isValidHexColor = (color: string): boolean => {
  return HEX_COLOR_REGEX.test(color.trim());
};

/**
 * 将 #color1,#color2 格式转换为 linear-gradient 格式
 * @param colorPair 颜色对，格式: "#302996,#bc1125"
 * @param angle 渐变角度，默认 90deg
 * @returns linear-gradient 格式字符串，如: "linear-gradient(90deg, #302996 0%, #bc1125 100%)"
 */
export const convertColorPairToGradient = (
  colorPair: string,
  angle: number = 90
): string | null => {
  if (!colorPair || typeof colorPair !== 'string') {
    return null;
  }

  // 检查是否是 #color1,#color2 格式（用逗号分隔的两个颜色值）
  if (!colorPair.includes(',')) {
    return null;
  }

  const colors = colorPair.split(',').map(c => c.trim());
  
  // 验证是否是有效的 HEX 颜色格式
  if (colors.length !== 2 || !colors.every(c => isValidHexColor(c))) {
    return null;
  }

  // 转换为 linear-gradient 格式
  return `linear-gradient(${angle}deg, ${colors[0]} 0%, ${colors[1]} 100%)`;
};

/**
 * 将 linear-gradient 格式转换为 #color1,#color2 格式
 * @param gradientString linear-gradient 格式字符串，如: "linear-gradient(90deg, #302996 0%, #bc1125 100%)"
 * @returns 颜色对格式字符串，如: "#302996,#bc1125"，如果解析失败返回 null
 */
export const convertGradientToColorPair = (gradientString: string): string | null => {
  if (!gradientString || typeof gradientString !== 'string') {
    return null;
  }

  // 检查是否是 linear-gradient 格式
  if (!gradientString.includes('linear-gradient')) {
    return null;
  }

  try {
    // 解析 linear-gradient 字符串，提取颜色值
    // 格式: linear-gradient(90deg, #302996 0%, #bc1125 100%)
    const regex = /linear-gradient\([^,]+,\s*(#[0-9A-Fa-f]{6})\s+\d+%,\s*(#[0-9A-Fa-f]{6})\s+\d+%\)/i;
    const match = gradientString.match(regex);
    
    if (match && match.length === 3) {
      // 提取两个颜色值，用逗号连接
      return `${match[1]},${match[2]}`;
    }
  } catch (error) {
    console.error('解析 linear-gradient 格式失败:', error);
  }

  return null;
};

/**
 * 检查字符串是否为 #color1,#color2 格式
 * @param value 待检查的字符串
 * @returns 是否为 #color1,#color2 格式
 */
export const isColorPairFormat = (value: string): boolean => {
  if (!value || typeof value !== 'string') {
    return false;
  }

  if (!value.includes(',')) {
    return false;
  }

  const colors = value.split(',').map(c => c.trim());
  return colors.length === 2 && colors.every(c => isValidHexColor(c));
};

/**
 * 检查字符串是否为 linear-gradient 格式
 * @param value 待检查的字符串
 * @returns 是否为 linear-gradient 格式
 */
export const isGradientFormat = (value: string): boolean => {
  return value && typeof value === 'string' && value.includes('linear-gradient');
};

/**
 * 将后端返回的昵称效果值转换为表单可用的格式
 * 支持格式：
 * - #color1,#color2 -> linear-gradient(90deg, #color1 0%, #color2 100%)
 * - linear-gradient(...) -> 保持不变
 * - JSON 格式 -> 转换为 linear-gradient（兼容旧格式）
 * @param value 后端返回的值
 * @returns 表单可用的 linear-gradient 格式字符串
 */
export const convertNicknameEffectForForm = (value: string | number | undefined): string => {
  if (!value) {
    return '';
  }

  const valueStr = value.toString();

  // 如果已经是 linear-gradient 格式，直接返回
  if (isGradientFormat(valueStr)) {
    return valueStr;
  }

  // 如果是 #color1,#color2 格式，转换为 linear-gradient
  if (isColorPairFormat(valueStr)) {
    const gradient = convertColorPairToGradient(valueStr);
    if (gradient) {
      return gradient;
    }
  }

  // 其他格式直接返回（可能是旧格式或其他格式）
  return valueStr;
};

/**
 * 将表单的昵称效果值转换为后端提交格式
 * 支持格式：
 * - linear-gradient(...) -> #color1,#color2
 * - #color1,#color2 -> 保持不变
 * - JSON 格式 -> 转换为 #color1,#color2（兼容旧格式）
 * @param value 表单的值
 * @param gradientConfigFromJson 从 JSON 转换为 CSS 的函数（可选，用于兼容旧格式）
 * @param gradientConfigToCss 从 JSON 转换为 CSS 的函数（可选，用于兼容旧格式）
 * @returns 后端提交用的 #color1,#color2 格式字符串
 */
export const convertNicknameEffectForSubmit = (
  value: string | number | undefined,
  gradientConfigFromJson?: (json: any) => any,
  gradientConfigToCss?: (config: any) => string
): string => {
  if (value === undefined || value === null) {
    return '';
  }

  if (typeof value !== 'string') {
    return '';
  }

  // 如果已经是 #color1,#color2 格式，直接返回
  if (isColorPairFormat(value)) {
    const colors = value.split(',').map(c => c.trim());
    return `${colors[0]},${colors[1]}`;
  }

  // 如果是 linear-gradient 格式，转换为 #color1,#color2
  if (isGradientFormat(value)) {
    const colorPair = convertGradientToColorPair(value);
    if (colorPair) {
      return colorPair;
    }
  }

  // 兼容旧格式：检查是否是 JSON 格式，转换为 CSS 后再提取颜色
  if (value.startsWith('{') || value.startsWith('"{\\"')) {
    if (gradientConfigFromJson && gradientConfigToCss) {
      try {
        let jsonStr = value;
        if (jsonStr.startsWith('"')) {
          jsonStr = JSON.parse(jsonStr);
        }
        const gradientConfig = gradientConfigFromJson(jsonStr);
        if (gradientConfig) {
          const cssString = gradientConfigToCss(gradientConfig);
          // 从 CSS 字符串中提取颜色
          const colorPair = convertGradientToColorPair(cssString);
          if (colorPair) {
            return colorPair;
          }
        }
      } catch (error) {
        console.error('解析 JSON 格式失败，保持原值:', error);
      }
    }
  }

  // 如果无法转换，返回原值
  return value;
};
